import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization/userOrganizationContext'

import { DashboardIntentStep } from '../../../../../../assets/images'
import { useOnboardingFormContext } from '../../../../contexts/OnboardingFormContext'
import { OnboardingUserIntent } from '../../../../enums/onboardingUserIntent'
import { usePredefinedOnboardingStepValue } from '../../../../hooks/usePredefinedOnboardingStepValue'
import { useRegisterOnboardingStep } from '../../../../hooks/useRegisterOnboardingStep'
import { useSubmitOnboardingStepForm } from '../../../../hooks/useSubmitOnboardingStepForm'
import { useTrackOnboarding } from '../../../../hooks/useTrackOnboarding'
import { CompanyOnboarding } from '../../../../types/companyOnboarding'
import { OnboardingForm } from '../../../../types/onboardingForm'
import { StepContainer } from '../StepContainer'
import { IntentTile } from './elements/IntentTile'
import * as Styled from './styles'
import { getIntentTiles } from './utils/getIntentTiles'

const intentAnalyticsNames: Record<OnboardingUserIntent, string> = {
  [OnboardingUserIntent.CreateInvoices]: 'invoice',
  [OnboardingUserIntent.TrackExpenses]: 'expense',
  [OnboardingUserIntent.ActivateEInvoices]: 'e-invoice',
  [OnboardingUserIntent.DiscoverAllFeatures]: 'discover',
}

const selectIntentValue = (onboarding?: CompanyOnboarding): Partial<OnboardingForm> => ({
  intents: onboarding?.userIntent ?? [],
})

const buildIntentPayload = (values: OnboardingForm) =>
  values.intents.length ? { userIntent: values.intents } : undefined

export const IntentTileSelectorStep = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { onboardingForm } = useOnboardingFormContext()
  const { clearErrors, FormItem } = onboardingForm
  const { trackOnboarding } = useTrackOnboarding()

  const { isLoading: isLoadingPredefinedValues, predefinedValues } = usePredefinedOnboardingStepValue(selectIntentValue)
  const { isSubmitting, submitForm } = useSubmitOnboardingStepForm(buildIntentPayload)

  const submitHandler = useCallback(
    async (values: OnboardingForm) => {
      await submitForm(values)
    },
    [submitForm],
  )

  useRegisterOnboardingStep({
    isLoading: isLoadingPredefinedValues,
    isSubmitting,
    predefinedValues,
    submitHandler,
  })

  const selectableTiles = useMemo(() => getIntentTiles(t, organization), [t, organization])

  const handleTileClick = useCallback(
    (
      intentId: OnboardingUserIntent,
      selectedIntents: OnboardingUserIntent[],
      onChange: (intents: OnboardingUserIntent[]) => void,
    ) => {
      const nextIntents = selectedIntents.includes(intentId)
        ? selectedIntents.filter((intent) => intent !== intentId)
        : [...selectedIntents, intentId]

      onChange(nextIntents)
      clearErrors('intents')
      trackOnboarding('Intent selected', {
        selectedIntents: nextIntents.map((intent) => intentAnalyticsNames[intent]),
      })
    },
    [clearErrors, trackOnboarding],
  )

  return (
    <StepContainer image={<DashboardIntentStep />} title={t('onboarding.steps.intent.title')}>
      <FormItem
        name="intents"
        render={({ field: { error, onChange, value } }) => (
          <Styled.TilesGrid>
            {selectableTiles.map(({ icon, id, label }) => (
              <IntentTile
                key={id}
                disabled={isLoadingPredefinedValues}
                hasError={!!error}
                icon={icon}
                id={id}
                isSelected={value.includes(id)}
                label={label}
                onClick={(intentId: OnboardingUserIntent) => handleTileClick(intentId, value, onChange)}
              />
            ))}
          </Styled.TilesGrid>
        )}
      />
    </StepContainer>
  )
}
